import {
  formatDurationLabel,
  guestPassDurationLabel,
  cleanSalePackageName,
  durationFromSalePackage,
  isValidIsoDate,
} from './saleDurationUtils';
import { membershipDurationDisplay } from '../../../utils/membershipDates';

const PERIOD_NOTE_RE = /(?:Duration|Renewed|Complimentary renewal)\s*:?\s*(.+?)\s*\((\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})/i;
/** Duration label without required dates — e.g. "Duration: 1 month" or "Renewed 3 months". */
const DURATION_ONLY_NOTE_RE = /(?:Duration|Renewed|Complimentary renewal)\s*:?\s*([^·(\n]+)/i;

/** Parse sale period stored in payment_note fallback text. */
export function parseSalePeriodFromNote(note) {
  const text = String(note || '');
  if (!text.trim()) return null;
  const match = text.match(PERIOD_NOTE_RE);
  if (match) {
    return {
      durationLabel: match[1].trim(),
      startDate: match[2],
      endDate: match[3],
    };
  }
  const loose = text.match(DURATION_ONLY_NOTE_RE);
  if (loose) {
    const durationLabel = String(loose[1] || '')
      .replace(/\s*,\s*membership\s+to\s+\d{4}-\d{2}-\d{2}.*$/i, '')
      .trim();
    if (durationLabel) return { durationLabel, startDate: '', endDate: '' };
  }
  return null;
}

function resolveDurationLabel(row, fromNote = null) {
  const source = String(row?.source || '').toLowerCase().replace(/-/g, '_');
  if (source === 'subscription' || source === 'renewal_sale' || source === 'renewal') {
    const membershipLike = membershipDurationDisplay({
      duration: row?.duration || row?.durationLabel || '',
      start: row?.startDate || row?.start || '',
      startDate: row?.startDate || row?.start || '',
      expiry: row?.endDate || row?.expiry || '',
      expiryDate: row?.endDate || row?.expiry || '',
      datesTbc: row?.datesTbc,
      lifetime: row?.lifetime,
      package: row?.package || '',
    });
    if (membershipLike && membershipLike !== '—') return membershipLike;
  }

  const explicit = String(row?.durationLabel || '').trim();
  if (explicit && explicit !== '—') return explicit;

  if (source === 'guest_pass') {
    const guest = guestPassDurationLabel(row);
    if (guest && guest !== '—') return guest;
  }

  const raw = String(row?.duration || '').trim();
  if (raw && raw.toLowerCase() !== 'tbc') {
    return formatDurationLabel(raw);
  }

  const fromPackage = durationFromSalePackage(row?.package || row?.item || row?.type || '');
  if (fromPackage) return fromPackage;

  const noteLabel = String(fromNote?.durationLabel || '').trim();
  if (noteLabel) return noteLabel;

  const fromDates = membershipDurationDisplay({
    start: row?.startDate || '',
    startDate: row?.startDate || '',
    expiry: row?.endDate || '',
    expiryDate: row?.endDate || '',
  });
  if (fromDates && fromDates !== '—') return fromDates;

  return '—';
}

export function getSaleRowPeriod(row) {
  const startDate = isValidIsoDate(row?.startDate) ? String(row.startDate).slice(0, 10) : '';
  const endDate = isValidIsoDate(row?.endDate) ? String(row.endDate).slice(0, 10) : '';
  const fromNote = parseSalePeriodFromNote(row?.paymentNote);
  if (startDate && endDate) {
    return {
      startDate,
      endDate,
      durationLabel: resolveDurationLabel(row, fromNote),
    };
  }
  if (fromNote) {
    return {
      ...fromNote,
      durationLabel: resolveDurationLabel(row, fromNote),
    };
  }
  return {
    startDate: startDate || '',
    endDate: endDate || '',
    durationLabel: resolveDurationLabel(row),
  };
}

export function saleRowDuration(row) {
  const { durationLabel } = getSaleRowPeriod(row);
  return durationLabel || '—';
}

/** Clean package/item for sales tables (duration shown separately). */
export function saleRowPackageName(row, fallback = '—') {
  return cleanSalePackageName(row?.package || row?.item || row?.type || '', fallback);
}

const TRAINER_SALE_SOURCES = new Set(['pt_sale', 'zumba_sale', 'yoga_sale']);

export function isTrainerSaleSource(source) {
  return TRAINER_SALE_SOURCES.has(String(source || '').trim().toLowerCase().replace(/-/g, '_'));
}

/** Remove "PT Sale — " style prefixes stored with the trainer/item name. */
export function stripSaleTypePrefix(text) {
  return String(text || '')
    .replace(
      /^(PT Sale|Zumba Sale|Yoga Sale|Spa Sale|Locker Sale|Oxybar Sale|Gym Apparel|Miscellaneous Sale|Renewal Sale|Guest Pass)\s*[—\-]\s*/i,
      '',
    )
    .trim();
}

/**
 * Trainer name for PT / Zumba / Yoga sales — stored in package_name, no extra column.
 */
export function saleRowTrainerName(row, fallback = '—') {
  const cleaned = saleRowPackageName(row, '');
  const stripped = stripSaleTypePrefix(cleaned);
  return stripped || cleaned || fallback;
}

export function saleRowStartDate(row) {
  const { startDate } = getSaleRowPeriod(row);
  return startDate || '—';
}

export function saleRowEndDate(row) {
  const { endDate } = getSaleRowPeriod(row);
  return endDate || '—';
}
